import { useState } from "react";
import { listStages } from "../../content/stages.js";
import type { AnalyticsSummary } from "./analyticsApi.js";
import { DailyDetailPanel } from "./DailyDetailPanel.js";
import { chartTickIndices } from "./chartTicks.js";
import { stageColor } from "./stageColors.js";

export type DailyMetric = "stages" | "players";
export type DailyBucket = { at: number; day: string; stages: Map<string, number>; playerCount: number };

type Props = {
  from: number;
  to: number;
  stageRows: AnalyticsSummary["daily"];
  playerRows: AnalyticsSummary["dailyPlayers"];
  metric: DailyMetric;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

function dayKey(milliseconds: number): string {
  return new Date(milliseconds + JST_OFFSET_MS).toISOString().slice(0, 10);
}

function bucketLabel(day: string): string {
  return `${Number(day.slice(5, 7))}月${Number(day.slice(8, 10))}日`;
}

function buildBuckets(from: number, to: number, stageRows: Props["stageRows"], playerRows: Props["playerRows"]): DailyBucket[] {
  const stageByDay = new Map<string, Map<string, number>>();
  for (const row of stageRows) {
    const values = stageByDay.get(row.day) ?? new Map<string, number>();
    values.set(row.stageId, row.runCount);
    stageByDay.set(row.day, values);
  }
  const playersByDay = new Map(playerRows.map((row) => [row.day, row.playerCount]));
  const first = Math.floor((from + JST_OFFSET_MS) / DAY_MS) * DAY_MS - JST_OFFSET_MS;
  return Array.from({ length: Math.ceil((to - first) / DAY_MS) }, (_, index) => {
    const at = first + index * DAY_MS;
    const day = dayKey(at);
    return { at, day, stages: stageByDay.get(day) ?? new Map<string, number>(), playerCount: playersByDay.get(day) ?? 0 };
  });
}

export function DailyChart({ from, to, stageRows, playerRows, metric }: Props) {
  const [selected, setSelected] = useState<number | null>(null);
  const stages = listStages();
  const buckets = buildBuckets(from, to, stageRows, playerRows);
  const total = (bucket: DailyBucket) => metric === "stages"
    ? [...bucket.stages.values()].reduce((sum, count) => sum + count, 0)
    : bucket.playerCount;
  const chartHeight = 176;
  const plotTop = 14;
  const plotBottom = 34;
  const plotHeight = chartHeight - plotTop - plotBottom;
  const max = Math.max(1, ...buckets.map(total));
  const step = Math.max(36, 276 / Math.max(1, buckets.length));
  const width = buckets.length * step + 84;
  const tickIndices = chartTickIndices(buckets.length, step);
  const selectedBucket = selected === null ? null : buckets.find((bucket) => bucket.at === selected) ?? null;

  return (
    <div className="analytics-daily">
      <div className="analytics-chart-scroll">
        <svg className="analytics-daily-chart" style={{ width: Math.max(360, width) }} viewBox={`0 0 ${width} ${chartHeight}`} role="img" aria-label={metric === "stages" ? "日別ラン開始数。色はステージごとの内訳です。" : "日別ユニークプレイヤー数。"}>
          <line className="analytics-chart-axis" x1="38" x2={width - 4} y1={plotTop + plotHeight} y2={plotTop + plotHeight} />
          <text className="analytics-chart-tick" x="32" y={plotTop + 4} textAnchor="end">{max}</text>
          {buckets.map((bucket, index) => {
            const x = 42 + index * step;
            let heightFromBottom = 0;
            const label = `${bucket.day}の内訳`;
            return (
              <g key={bucket.at} role="button" aria-label={label} aria-pressed={selected === bucket.at} className={selected === bucket.at ? "analytics-bar is-selected" : "analytics-bar"} onClick={() => setSelected(selected === bucket.at ? null : bucket.at)}>
                <title>{metric === "stages" ? `${bucket.day}：${stages.map((stage) => `${stage.name} ${bucket.stages.get(stage.id) ?? 0}`).join("、")}` : `${bucket.day}：ユニークプレイヤー ${bucket.playerCount}`}</title>
                <rect className="analytics-bar-hit" x={x - 6} y={plotTop} width="36" height={plotHeight} />
                {metric === "stages" ? stages.map((stage, stageIndex) => {
                  const count = bucket.stages.get(stage.id) ?? 0;
                  const height = plotHeight * count / max;
                  const y = plotTop + plotHeight - heightFromBottom - height;
                  heightFromBottom += height;
                  return <rect key={stage.id} fill={stageColor(stageIndex)} x={x} y={y} width="22" height={height} rx="2" />;
                }) : <rect className="analytics-bar-player" x={x} y={plotTop + plotHeight - plotHeight * bucket.playerCount / max} width="22" height={plotHeight * bucket.playerCount / max} rx="2" />}
                {tickIndices.has(index) && <text className="analytics-chart-label" x={x + 11} y={chartHeight - 13} textAnchor="middle">{bucketLabel(bucket.day)}</text>}
              </g>
            );
          })}
        </svg>
      </div>
      {metric === "stages" && <div className="analytics-stage-legend">{stages.map((stage, index) => <span key={stage.id}><i style={{ background: stageColor(index) }} />{stage.name}</span>)}</div>}
      {selectedBucket && <DailyDetailPanel bucket={selectedBucket} metric={metric} onClose={() => setSelected(null)} />}
    </div>
  );
}
